import { useState, useCallback, useEffect, useRef } from 'react';
import { downloadStorage, DownloadRecord } from '@/lib/download-storage';

interface VideoQuality {
  id: number;
  quality: string;
  mime: string;
  size: string;
  type: string;
  isMobileCompatible?: boolean;
}

interface DownloadProgress {
  videoId: string;
  status: 'pending' | 'downloading' | 'processing' | 'completed' | 'failed';
  progress: number;
  quality?: string;
  fileUrl?: string;
  error?: string;
  timestamp: number;
  retryCount?: number;
}

interface UseVideoDownloadWithRetryReturn {
  qualities: VideoQuality[];
  isLoadingQualities: boolean;
  downloadProgress: DownloadProgress | null;
  isDownloading: boolean;
  downloadedUrl: string | null;
  error: string | null;
  retryCount: number;
  canRetry: boolean;
  fetchQualities: (videoId: string) => Promise<void>;
  startDownload: (videoId: string, qualityId: number, quality: string) => Promise<void>;
  retryDownload: () => Promise<void>;
  checkProgress: (videoId: string) => Promise<void>;
  resetDownload: () => void;
  // Enhanced features
  getExistingDownload: (videoId: string) => any | null;
  hasExistingDownload: (videoId: string) => boolean;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAYS = [2000, 5000, 10000]; // Progressive delays: 2s, 5s, 10s

export function useVideoDownloadWithRetry(): UseVideoDownloadWithRetryReturn {
  const [qualities, setQualities] = useState<VideoQuality[]>([]);
  const [isLoadingQualities, setIsLoadingQualities] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadedUrl, setDownloadedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentVideoIdRef = useRef<string | null>(null);
  const currentQualityRef = useRef<{ id: number; quality: string } | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const canRetry = retryCount < MAX_RETRY_ATTEMPTS;

  // Enhanced error handling with categorization
  const categorizeError = (error: string): {
    category: 'network' | 'server' | 'quota' | 'video' | 'storage' | 'unknown';
    isRetryable: boolean;
    userMessage: string;
  } => {
    const errorLower = error.toLowerCase();
    
    if (errorLower.includes('network') || errorLower.includes('timeout') || errorLower.includes('fetch')) {
      return {
        category: 'network',
        isRetryable: true,
        userMessage: 'Network connection issue. Please check your internet connection and try again.'
      };
    }
    
    if (errorLower.includes('quota') || errorLower.includes('rate limit')) {
      return {
        category: 'quota',
        isRetryable: true,
        userMessage: 'Download service temporarily busy. Please try again in a few minutes.'
      };
    }
    
    if (errorLower.includes('not available') || errorLower.includes('no video') || errorLower.includes('video not found')) {
      return {
        category: 'video',
        isRetryable: false,
        userMessage: 'This video is not available for download. It may be private or removed.'
      };
    }
    
    if (errorLower.includes('storage') || errorLower.includes('blob')) {
      return {
        category: 'storage',
        isRetryable: true,
        userMessage: 'Storage error occurred. Please try again.'
      };
    }
    
    if (errorLower.includes('500') || errorLower.includes('server')) {
      return {
        category: 'server',
        isRetryable: true,
        userMessage: 'Server error. Please try again in a moment.'
      };
    }
    
    return {
      category: 'unknown',
      isRetryable: true,
      userMessage: 'An unexpected error occurred. Please try again.'
    };
  };

  // Fetch available qualities with retry
  const fetchQualities = useCallback(async (videoId: string, retryAttempt = 0) => {
    setIsLoadingQualities(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/youtube/download?videoId=${videoId}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch video qualities');
      }
      
      const data = await response.json();
      console.log('📥 Received qualities data:', {
        videoId,
        qualitiesCount: data.qualities?.length || 0,
        dataKeys: Object.keys(data)
      });
      setQualities(data.qualities || []);
      
      // Check if there's existing progress for this video
      await checkProgress(videoId);
      
    } catch (err) {
      console.error('Error fetching qualities:', err);
      const errorInfo = categorizeError(err instanceof Error ? err.message : 'Unknown error');
      
      // Retry for retryable errors
      if (errorInfo.isRetryable && retryAttempt < MAX_RETRY_ATTEMPTS) {
        console.log(`Retrying fetchQualities (attempt ${retryAttempt + 1}/${MAX_RETRY_ATTEMPTS})`);
        
        const delay = RETRY_DELAYS[retryAttempt] || 10000;
        retryTimeoutRef.current = setTimeout(() => {
          fetchQualities(videoId, retryAttempt + 1);
        }, delay);
        
        return;
      }
      
      setError(errorInfo.userMessage);
      setQualities([]);
    } finally {
      setIsLoadingQualities(false);
    }
  }, []);

  // Check download progress with enhanced error handling
  const checkProgress = useCallback(async (videoId: string) => {
    try {
      const response = await fetch(`/api/youtube/download?videoId=${videoId}&action=progress`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.progress) {
          setDownloadProgress(data.progress);
          
          if (data.progress.status === 'completed' && data.progress.fileUrl) {
            setDownloadedUrl(data.progress.fileUrl);
            setIsDownloading(false);
            setRetryCount(0); // Reset retry count on success
            
            // Save to local storage
            const downloadRecord: DownloadRecord = {
              videoId,
              quality: data.progress.quality || 'unknown',
              fileUrl: data.progress.fileUrl,
              fileName: `${videoId}_${data.progress.quality || 'unknown'}.mp4`,
              title: currentVideoIdRef.current === videoId ? 'Downloaded Video' : 'Downloaded Video',
              author: 'Unknown',
              thumbnail: '',
              downloadedAt: Date.now(),
              watchCount: 0
            };
            
            downloadStorage.addDownload(downloadRecord);
            
            // Clear interval if running
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
          } else if (data.progress.status === 'failed') {
            const errorInfo = categorizeError(data.progress.error || 'Download failed');
            setError(errorInfo.userMessage);
            setIsDownloading(false);
            
            // Clear interval if running
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
          }
        }
      }
    } catch (err) {
      console.error('Error checking progress:', err);
      // Don't set error for progress check failures, they might be temporary
    }
  }, []);

  // Start video download with enhanced retry logic
  const startDownload = useCallback(async (videoId: string, qualityId: number, quality: string) => {
    setIsDownloading(true);
    setError(null);
    setRetryCount(0);
    setDownloadProgress({
      videoId,
      status: 'pending',
      progress: 0,
      quality,
      timestamp: Date.now(),
      retryCount: 0
    });
    
    currentVideoIdRef.current = videoId;
    currentQualityRef.current = { id: qualityId, quality };
    
    try {
      // Start progress polling
      progressIntervalRef.current = setInterval(() => {
        checkProgress(videoId);
      }, 2000); // Check every 2 seconds
      
      const response = await fetch('/api/youtube/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId,
          qualityId,
          quality,
          userId: localStorage.getItem('userId') // Optional user tracking
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download video');
      }
      
      const data = await response.json();
      
      if (data.downloadUrl) {
        setDownloadedUrl(data.downloadUrl);
        setDownloadProgress({
          videoId,
          status: 'completed',
          progress: 100,
          quality,
          fileUrl: data.downloadUrl,
          timestamp: Date.now(),
          retryCount: 0
        });
        
        // Save to local storage
        const downloadRecord: DownloadRecord = {
          videoId,
          quality,
          fileUrl: data.downloadUrl,
          fileName: `${videoId}_${quality}.mp4`,
          title: data.title || 'Downloaded Video',
          author: data.author || 'Unknown',
          thumbnail: data.thumbnail || '',
          downloadedAt: Date.now(),
          watchCount: 0
        };
        
        downloadStorage.addDownload(downloadRecord);
      }
      
    } catch (err) {
      console.error('Error downloading video:', err);
      const errorInfo = categorizeError(err instanceof Error ? err.message : 'Unknown error');
      
      setError(errorInfo.userMessage);
      setDownloadProgress(prev => prev ? {
        ...prev,
        status: 'failed',
        error: errorInfo.userMessage,
        retryCount: retryCount
      } : null);
      
      setRetryCount(prev => prev + 1);
      
    } finally {
      setIsDownloading(false);
      
      // Clear interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  }, [retryCount, checkProgress]);

  // Retry download
  const retryDownload = useCallback(async () => {
    if (!canRetry || !currentVideoIdRef.current || !currentQualityRef.current) {
      return;
    }
    
    console.log(`Retrying download (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
    
    const delay = RETRY_DELAYS[retryCount] || 10000;
    
    // Show retry delay to user
    setError(`Retrying in ${delay / 1000} seconds...`);
    
    retryTimeoutRef.current = setTimeout(() => {
      startDownload(
        currentVideoIdRef.current!,
        currentQualityRef.current!.id,
        currentQualityRef.current!.quality
      );
    }, delay);
    
  }, [canRetry, retryCount, startDownload]);

  // Reset download state
  const resetDownload = useCallback(() => {
    setDownloadProgress(null);
    setDownloadedUrl(null);
    setError(null);
    setIsDownloading(false);
    setRetryCount(0);
    
    currentVideoIdRef.current = null;
    currentQualityRef.current = null;
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Check for existing download from localStorage (for backward compatibility)
  const getExistingDownload = useCallback((videoId: string): any | null => {
    try {
      const downloads = JSON.parse(localStorage.getItem('video_downloads') || '{}');
      return downloads.downloads?.find((d: any) => d.videoId === videoId) || null;
    } catch {
      return null;
    }
  }, []);

  const hasExistingDownload = useCallback((videoId: string): boolean => {
    const existing = getExistingDownload(videoId);
    return existing !== null && Boolean(existing.fileUrl);
  }, [getExistingDownload]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    qualities,
    isLoadingQualities,
    downloadProgress,
    isDownloading,
    downloadedUrl,
    error,
    retryCount,
    canRetry,
    fetchQualities,
    startDownload,
    retryDownload,
    checkProgress,
    resetDownload,
    getExistingDownload,
    hasExistingDownload
  };
}